/**
 * ============================================================================
 * RACE ENGINE CORE - DATA STRUCTURES & ALGORITHMS
 * ============================================================================
 * Lógica pura para la gestión de la matriz de turnos, rachas de fatiga,
 * validación de aptitudes y disponibilidad estacional de carreras.
 */

export const PHASES = {
  JUNIOR: 'Junior',
  CLASSIC: 'Classic',
  SENIOR: 'Senior'
};

export const STREAK_LEVELS = {
  SAFE: { level: 0, tag: 'SAFE', label: 'Seguro (Óptimo)' },
  WARNING: { level: 1, tag: 'WARNING', label: 'Advertencia Menor' },
  STRESS: { level: 2, tag: 'STRESS', label: 'Riesgo Alto (Estrés)' },
  PENALTY: { level: 3, tag: 'PENALTY', label: 'Riesgo Extremo (Penalización)' }
};

/**
 * Genera la matriz bidimensional plana de turnos (Bloques de tiempo).
 * Total: 72 turnos (24 por año).
 * @returns {Array<Object>} Arreglo de turnos inicializados y vacíos.
 */
export const initializeTimelineMatrix = () => {
  const timeline = [];
  const TOTAL_TURNS = 72;

  for (let id = 1; id <= TOTAL_TURNS; id++) {
    const isJunior = id >= 1 && id <= 24;
    const isClassic = id >= 25 && id <= 48;
    const isSenior = id >= 49 && id <= 72;

    let currentPhase = '';
    if (isJunior) currentPhase = PHASES.JUNIOR;
    else if (isClassic) currentPhase = PHASES.CLASSIC;
    else if (isSenior) currentPhase = PHASES.SENIOR;

    const turnInYear = ((id - 1) % 24) + 1;
    const month = Math.ceil(turnInYear / 2);
    const half = (turnInYear % 2 !== 0) ? 1 : 2;

    const isPlayable = isJunior ? id >= 13 : true;

    timeline.push({
      id,
      phase: currentPhase,
      month,
      half,
      isPlayable,
      scheduledRace: null
    });
  }

  return timeline;
};

/**
 * Registra y calcula rachas hacia adelante y atrás para un target dado.
 */
export const calculateConsecutivePenalty = (timeline, targetTurnId) => {
  let consecutiveCount = 1;

  for (let i = targetTurnId - 1; i >= 1; i--) {
    const pastTurn = timeline[i - 1];
    if (pastTurn && pastTurn.scheduledRace !== null) {
      consecutiveCount++;
    } else {
      break;
    }
  }

  for (let i = targetTurnId + 1; i <= 72; i++) {
    const futureTurn = timeline[i - 1];
    if (futureTurn && futureTurn.scheduledRace !== null) {
      consecutiveCount++;
    } else {
      break;
    }
  }

  if (consecutiveCount >= 4) return STREAK_LEVELS.PENALTY;
  if (consecutiveCount === 3) return STREAK_LEVELS.STRESS;
  if (consecutiveCount === 2) return STREAK_LEVELS.WARNING;
  
  return STREAK_LEVELS.SAFE;
};

/**
 * Utilidad extra: Analiza globalmente toda la Matrix y retorna el peor nivel
 * útil para mostrar un ícono de alerta global.
 */
export const getGlobalTimelineStress = (timeline) => {
  let maxStreak = 0;
  let currentStreak = 0;

  for (let turn of timeline) {
    if (turn.scheduledRace !== null) {
      currentStreak++;
      if (currentStreak > maxStreak) {
        maxStreak = currentStreak;
      }
    } else {
      currentStreak = 0;
    }
  }

  if (maxStreak >= 4) return STREAK_LEVELS.PENALTY;
  if (maxStreak === 3) return STREAK_LEVELS.STRESS;
  if (maxStreak === 2) return STREAK_LEVELS.WARNING;
  
  return STREAK_LEVELS.SAFE;
};

/**
 * Filtra qué carreras están disponibles en un turno específico a partir de la DB de la API.
 * Extrae la lógica cruzando contra race.schedules.
 * La API ya devuelve sched.turn (número absoluto 1-72), idéntico al turn.id del timeline.
 */
export const getAvailableRacesForTurn = (turnData, allRacesDB) => {
  if (!turnData.isPlayable || !allRacesDB) return [];

  return allRacesDB.filter(race => {
    if (!race.schedules || !Array.isArray(race.schedules)) return false;
    return race.schedules.some(sched => Number(sched.turn) === turnData.id);
  });
};

/**
 * Mapa canónico de distance_type de la API a las keys reales del objeto de aptitudes.
 * La API devuelve: 'Short', 'Mile', 'Medium', 'Long'
 * Las keys de aptitudes son:  short_distance, mile, medium_distance, long_distance
 */
const DISTANCE_TYPE_MAP = {
  'short': 'short_distance',
  'mile': 'mile',
  'medium': 'medium_distance',
  'long': 'long_distance'
};

/**
 * Validador estricto de viabilidad de terreno y distancia usando la estructura API.
 * SIEMPRE exige aptitud 'A' en terreno Y en distancia para considerar una carrera viable.
 */
export const checkAptitudeViability = (characterAptitudes, race) => {
  if (!characterAptitudes || !race || !race.course) return false;

  const surfaceKey = String(race.course.surface).toLowerCase();           // 'turf' | 'dirt'
  const distanceRaw = String(race.course.distance_type).toLowerCase();    // 'sprint' | 'mile' | 'medium' | 'long'
  const distanceKey = DISTANCE_TYPE_MAP[distanceRaw];                     // key real en el objeto

  if (!distanceKey) return false; // distancia desconocida → descartar

  const valid = ['S', 'A'];
  return valid.includes(characterAptitudes[surfaceKey]) && valid.includes(characterAptitudes[distanceKey]);
};

/**
 * Devuelve los fans_earned de una carrera para un turno específico.
 * Cada carrera puede aparecer en múltiples turnos con valores distintos.
 */
const getFansForTurn = (race, turnId) => {
  const sched = race.schedules?.find(s => Number(s.turn) === turnId);
  return sched?.fans_earned || 0;
};

/**
 * 6. Auto-Optimizador de Ruta (2-Pass Global Optimizer)
 *
 * Pasada 1 — Greedy base sin triples: rellena el timeline eligiendo la carrera
 * de mayor fans_earned disponible en cada turno, respetando el límite de 2 consecutivas.
 *
 * Pasada 2 — Asignación global de cupos triple: encuentra TODOS los descansos
 * forzados del schedule base, calcula el net_gain de convertir cada uno en triple
 * (fans_ganados − fans_perdidos_por_forceRest), ordena por net_gain y asigna los
 * cupos a los mejores globalmente. Esto evita que el cupo se consuma en un G3 de
 * enero cuando hay un clúster de G1 en mayo que valdría mucho más.
 *
 * Garantía: añadir un cupo de triple nunca reduce el total de fans (solo se aplica
 * si net_gain > 0).
 *
 * @param {Array<Object>} baseTimeline - Array puro inicializado (scheduledRace: null)
 * @param {Object} characterAptitudes - Aptitudes del personaje { turf, dirt, mile, ... }
 * @param {Array<Object>} allRacesDB   - Catálogo completo de carreras con schedules
 * @param {Object} filterPreferences  - { summerVacationEnabled, maxTriples, onlyG1G2 }
 * @returns {Array<Object>} Timeline optimizado
 */
export const autoOptimizeRoute = (baseTimeline, characterAptitudes, allRacesDB, filterPreferences = {}) => {
  const {
    summerVacationEnabled = false,
    maxTriples = 0,
    onlyG1G2 = false
  } = filterPreferences;

  const viableRaces = allRacesDB.filter(race => checkAptitudeViability(characterAptitudes, race));

  const isVacation = (turn) =>
    summerVacationEnabled && (turn.month === 7 || turn.month === 8) && turn.phase !== 'Junior';

  const pickBest = (turn) => {
    const available = getAvailableRacesForTurn(turn, viableRaces);
    if (!available.length) return null;
    available.sort((a, b) => getFansForTurn(b, turn.id) - getFansForTurn(a, turn.id));
    const best = available[0];
    if (onlyG1G2 && best.grade !== 'G1' && best.grade !== 'G2') return null;
    if (getFansForTurn(best, turn.id) === 0) return null;
    return best;
  };

  // ── PASADA 1: Schedule base sin triples ──────────────────────────────────
  const schedule = baseTimeline.map(t => ({ ...t, scheduledRace: null }));

  for (let i = 0; i < schedule.length; i++) {
    const turn = schedule[i];
    if (!turn.isPlayable || isVacation(turn)) continue;

    const prev1 = i > 0 ? schedule[i - 1].scheduledRace : null;
    const prev2 = i > 1 ? schedule[i - 2].scheduledRace : null;
    if ((prev1 ? 1 : 0) + (prev1 && prev2 ? 1 : 0) >= 2) continue; // límite consecutivas

    const best = pickBest(turn);
    if (best) schedule[i].scheduledRace = best;
  }

  if (maxTriples === 0) return schedule;

  // ── PASADA 2: Identificar y rankear todos los descansos forzados ─────────
  const candidates = [];

  for (let i = 0; i < schedule.length; i++) {
    const turn = schedule[i];
    if (!turn.isPlayable || isVacation(turn) || turn.scheduledRace !== null) continue;

    const prev1 = i > 0 ? schedule[i - 1].scheduledRace : null;
    const prev2 = i > 1 ? schedule[i - 2].scheduledRace : null;
    if ((prev1 ? 1 : 0) + (prev1 && prev2 ? 1 : 0) < 2) continue; // no es descanso forzado

    const tripleRace = pickBest(turn);
    if (!tripleRace) continue;

    const gain = getFansForTurn(tripleRace, turn.id);
    // Coste: lo que perdemos al anular el turno i+1 con forceRest
    const nextSlot = i + 1 < schedule.length ? schedule[i + 1] : null;
    const cost = nextSlot?.scheduledRace ? getFansForTurn(nextSlot.scheduledRace, nextSlot.id) : 0;

    candidates.push({ i, race: tripleRace, net: gain - cost });
  }

  // Ordenar por net gain descendente
  candidates.sort((a, b) => b.net - a.net);

  // Seleccionar los mejores cupos sin conflictos entre sí (solo net > 0)
  const tripleAt = new Set();
  const restAt = new Set();

  for (const c of candidates) {
    if (tripleAt.size >= maxTriples) break;
    if (c.net <= 0) break; // garantiza que más triples = más fans
    if (restAt.has(c.i)) continue; // este slot fue anulado por un triple anterior
    if (tripleAt.has(c.i + 1)) continue; // el siguiente ya es un triple (no puede haber cuádruple)
    tripleAt.add(c.i);
    if (c.i + 1 < schedule.length) restAt.add(c.i + 1);
  }

  // ── PASADA 3: Aplicar triples y descansos forzados al schedule base ──────
  for (const c of candidates) {
    if (tripleAt.has(c.i)) schedule[c.i].scheduledRace = c.race;
  }
  for (const i of restAt) {
    if (i < schedule.length) schedule[i].scheduledRace = null;
  }

  return schedule;
};
